import { useInternationalization, useNotification } from "@/lib/contexts";
import {
  type CopyAlertSchedulesResponse,
  copyAlertSchedulesToOwnerRooms,
} from "@/services/API/rooms";
import { useQueryClient } from "@tanstack/react-query";
import { useMutationWrapper } from "../../wrappers";
import { roomDetailsKey, roomListKey } from "../room-query-keys";

export interface CopyAlertSchedulesMutationParams {
  roomId: string;
  onSuccess?: (updatedRooms: number) => void;
  onError?: () => void;
}

export function useMutationCopyAlertSchedules({
  roomId,
  onSuccess,
  onError,
}: CopyAlertSchedulesMutationParams) {
  const { t } = useInternationalization("pages.rooms.details.settings.alert-schedules");
  const { success, error } = useNotification();
  const queryClient = useQueryClient();

  const invalidateQueryKeys = () => {
    queryClient.invalidateQueries({ queryKey: roomDetailsKey(roomId) });
    queryClient.invalidateQueries({ queryKey: roomListKey() });
  };

  const handleSuccess = (data: CopyAlertSchedulesResponse) => {
    success(t("copy-success", { count: data.updatedRooms }));
    invalidateQueryKeys();
    onSuccess?.(data.updatedRooms);
  };

  const handleError = () => {
    error(t("copy-error"));
    onError?.();
  };

  const { mutate, mutateAsync, isPending } = useMutationWrapper<CopyAlertSchedulesResponse, string>(
    {
      mutationKey: ["rooms", "copy-alert-schedules", roomId],
      mutationFn: copyAlertSchedulesToOwnerRooms,
      onSuccess: handleSuccess,
      onError: handleError,
    }
  );

  return {
    copyAlertSchedules: mutate,
    copyAlertSchedulesAsync: mutateAsync,
    isPending,
  };
}
